import React from 'react';
import { Box, Typography, Chip, SxProps, useMediaQuery } from '@mui/material';
import ReportNasemScoreBadge from './ReportNasemScoreBadge';
import { formatDateWithOrdinalSuffix } from '../utils';
import { getNasemScoreConfig } from '../utils/reports';
import { useNavigate } from 'react-router-dom';
import { ReportCardResult } from '../types/reports';
import { useThemeContext } from '../contexts/BaseThemeContextProvider';

interface ResultCardProps {
  result: ReportCardResult;
  onClick: () => void;
  additionaStyles?: SxProps;
}

const ResultCard = ({ result, onClick, additionaStyles = {} }: ResultCardProps) => {
  const { theme } = useThemeContext();
  const { exposureTextColor } = getNasemScoreConfig(Number(result.nasemScore), theme);
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        background: theme.custom.report.details.resultCard.backgroundColor,
        borderRadius: '16px',
        padding: { xs: '10px 16px', smMd800: '16px' },
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        ...additionaStyles,
      }}
      onClick={onClick}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {/* Date */}
        <Chip
          label={formatDateWithOrdinalSuffix(result.report_date, { month: 'short', day: 'numeric', year: 'numeric' })}
          sx={{
            bgcolor: theme.custom.report.details.resultCard.dateChip.backgroundColor,
            borderRadius: '4px',
            color: theme.custom.report.details.resultCard.dateChip.color,
            fontWeight: 500,
            fontSize: { xs: '12px', smMd800: '14px' },
          }}
        />

        {/* NASEM Level */}
        <Typography
          sx={{
            marginLeft: { xs: '9px', sm: 'auto' },
            textAlign: { xs: 'center', sm: 'right' },
            marginRight: '8px',
            fontSize: { xs: '11px', smMd800: '14px' },
            fontWeight: 500,
            color: exposureTextColor,
          }}
        >
          NASEM level {result.nasemLevel}
        </Typography>

        {/* NASEM Score Badge*/}
        <ReportNasemScoreBadge
          showUnit={false}
          showBorder={false}
          nasemScore={Number(result.nasemScore)}
          additionalBoxStyles={{ padding: { xs: '2px 8px', smMd800: '2px 8px' } }}
          additionalTypographyStyles={{ fontSize: { xs: '14px', smMd800: '22px' } }}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {/* Compounds Detected */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexBasis: { xs: '45%', sm: 'auto' },
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: '22px', smMd800: '35px' },
              fontWeight: 500,
              lineHeight: '52px',
              color: theme.custom.report.details.resultCard.compoundsDetected.detectedCountTextColor,
              marginRight: '4px',
            }}
          >
            {result.totalCompoundsDetected}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '12px', smMd800: '14px' },
              fontWeight: 400,
              color: theme.custom.report.details.resultCard.compoundsDetected.compoundDetectedTextColor,
              lineHeight: '15px',
              marginTop: '-3px',
              textDecorationLine: 'underline',
              textDecorationStyle: 'solid',
              textDecorationSkipInk: 'auto',
              textDecorationThickness: 'auto',
              textUnderlineOffset: 'auto',
              textUnderlinePosition: 'from-font',
              cursor: 'pointer',
            }}
            onClick={onClick}
          >
            {isMobile ? 'PFAS detectable' : 'PFAS above detection level' }
          </Typography>
        </Box>

        {/* Divider Line */}
        <Box
          sx={{
            width: '2px',
            height: '40px',
            margin: {
              xs: '0 8px',
              sm: '0 16px',
            },
            backgroundColor: theme.custom.report.details.resultCard.dividerLineColor,
            borderRadius: '4px',
          }}
        />

        {/* Total PFAS */}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexBasis: { xs: '40%', sm: 'auto' },
            justifyContent: 'flex-end',
            gap: '4px',
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: '22px', smMd800: '35px' },
              fontWeight: 400,
              lineHeight: '52px',
              color: theme.custom.report.details.resultCard.totalPfas.totalPfasScoreTextColor,
            }}
          >
            {result.totalPFASScore.toFixed(1)}
          </Typography>
          <Typography
            sx={{
              color: theme.custom.report.details.resultCard.totalPfas.pfasUnitColor,
              fontSize: { xs: '12px', smMd800: '14px' },
              fontWeight: 400,
              lineHeight: '15px',
              marginTop: '-3px',
            }}
          >
            ng/mL{' '}
            <Typography
              component="span"
              sx={{
                display: { xs: 'block', sm: 'inline' },
                color: theme.custom.report.details.resultCard.totalPfas.totalPfasTextColor,
                fontSize: { xs: '12px', smMd800: '14px' },
                fontWeight: 400,
                lineHeight: '18px',
                textDecorationLine: 'underline',
                textDecorationStyle: 'solid',
                textDecorationSkipInk: 'auto',
                textDecorationThickness: 'auto',
                textUnderlineOffset: 'auto',
                textUnderlinePosition: 'from-font',
                cursor: 'pointer',
              }}
              onClick={() => navigate('/pfas-info.html?activeTab=0')}
            >
              {isMobile ? 'total PFAS': 'total PFAS measurable'}
            </Typography>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ResultCard;
